import { useRef, useState } from "react";
import { FileUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAddPapers } from "@/hooks/usePapers";
import { parseReferences, type ParseResult } from "@/lib/reference-parser";
import type { PaperBatchResult } from "@/types/paper";

type Stage = "select" | "preview" | "done";

interface Props {
  open: boolean;
  onClose: () => void;
  conceptId: string;
}

export function ImportPapersDialog({ open, onClose, conceptId }: Props) {
  const [stage, setStage] = useState<Stage>("select");
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [result, setResult] = useState<PaperBatchResult | null>(null);
  const [error, setError] = useState("");
  const [showSkipped, setShowSkipped] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addPapers = useAddPapers(conceptId);

  function reset() {
    setStage("select");
    setParsed(null);
    setResult(null);
    setError("");
    setShowSkipped(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleOpenChange(v: boolean) {
    if (!v) {
      reset();
      onClose();
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      const parseResult = parseReferences(content);
      setParsed(parseResult);
      setStage("preview");
      setError("");
    } catch {
      setError("Failed to read the file.");
    }
  }

  async function handleImport() {
    if (!parsed || parsed.papers.length === 0) return;

    try {
      const batchResult = await addPapers.mutateAsync({
        papers: parsed.papers,
        role: "foundational",
        added_source: "manual",
      });
      setResult(batchResult);
      setStage("done");
    } catch {
      setError("Failed to import papers.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {stage === "select" && "Import Papers from File"}
            {stage === "preview" && "Preview Import"}
            {stage === "done" && "Import Complete"}
          </DialogTitle>
        </DialogHeader>

        {/* Stage 1: Select file */}
        {stage === "select" && (
          <div className="space-y-4 py-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 text-muted-foreground transition-colors hover:border-muted-foreground/50 hover:text-foreground"
            >
              <FileUp className="size-8" />
              <span className="text-sm font-medium">
                Click to select a .txt file
              </span>
              <span className="text-xs">
                One reference per line (APA format)
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.bib"
              className="hidden"
              onChange={handleFileSelect}
            />
            <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
              <p className="font-medium">Expected format:</p>
              <p className="mt-1 font-mono">
                Author, A., & Author, B. (2024). Paper title. Journal Name, 1(2), 10-20.
              </p>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        {/* Stage 2: Preview */}
        {stage === "preview" && parsed && (
          <div className="space-y-4 py-4">
            <div className="flex gap-4 text-sm">
              <span className="font-medium text-foreground">
                {parsed.papers.length} paper{parsed.papers.length !== 1 ? "s" : ""} found
              </span>
              {parsed.skippedLines.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowSkipped(!showSkipped)}
                  className="flex items-center gap-1 text-amber-600 hover:underline dark:text-amber-400"
                >
                  <AlertTriangle className="size-3.5" />
                  {parsed.skippedLines.length} line{parsed.skippedLines.length !== 1 ? "s" : ""} skipped
                </button>
              )}
            </div>

            {showSkipped && parsed.skippedLines.length > 0 && (
              <div className="max-h-24 overflow-y-auto rounded-md bg-muted p-2 text-xs text-muted-foreground">
                {parsed.skippedLines.map((line, i) => (
                  <p key={i} className="truncate">{line}</p>
                ))}
              </div>
            )}

            <div className="max-h-64 overflow-y-auto space-y-2">
              {parsed.papers.map((paper, i) => (
                <div key={i} className="rounded-md border p-2 text-sm">
                  <p className="font-medium leading-tight">{paper.title}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {paper.authors?.join(", ")}
                    {paper.year ? ` (${paper.year})` : ""}
                    {paper.journal ? ` — ${paper.journal}` : ""}
                  </p>
                </div>
              ))}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        {/* Stage 3: Done */}
        {stage === "done" && result && (
          <div className="flex flex-col items-center gap-3 py-8">
            <CheckCircle2 className="size-10 text-green-600 dark:text-green-400" />
            <div className="text-center text-sm">
              <p><span className="font-medium">{result.added}</span> paper{result.added !== 1 ? "s" : ""} added</p>
              {result.duplicates > 0 && (
                <p className="text-muted-foreground">
                  {result.duplicates} duplicate{result.duplicates !== 1 ? "s" : ""} skipped
                </p>
              )}
              {result.merged > 0 && (
                <p className="text-muted-foreground">
                  {result.merged} merged with existing
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {stage === "select" && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          {stage === "preview" && (
            <>
              <Button variant="outline" onClick={() => { reset(); }}>
                Back
              </Button>
              <Button
                onClick={handleImport}
                disabled={addPapers.isPending || !parsed || parsed.papers.length === 0}
              >
                {addPapers.isPending
                  ? "Importing..."
                  : `Import ${parsed?.papers.length ?? 0} Paper${(parsed?.papers.length ?? 0) !== 1 ? "s" : ""}`}
              </Button>
            </>
          )}
          {stage === "done" && (
            <Button onClick={() => { reset(); onClose(); }}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
